<script lang="ts">
  import { onMount } from 'svelte';
  import Header from './lib/components/Header.svelte';
  import HumanVerify from './lib/components/HumanVerify.svelte';
  import Selection from './lib/pages/Selection.svelte';
  import ChecklistView from './lib/pages/ChecklistView.svelte';
  import PrintView from './lib/pages/PrintView.svelte';
  import {
    session, currentStep, records, selectedChecklistId, fontSize,
    storageMode, storageSyncing, storageAdapter, committeeComments
  } from './lib/stores';
  import { ROLE_LABELS } from './lib/types';
  import { initStorage } from './lib/storage';
  import type { StorageAdapter } from './lib/storage';
  import type { StoragePayload } from './lib/storage/types';

  // 預先從 localStorage 或 sessionStorage 判定是否已驗證
  let initialVerified = false;
  try {
    if (typeof window !== 'undefined') {
      const savedName = localStorage.getItem('dmy_name');
      if (savedName && savedName.trim()) initialVerified = true;
      if (!initialVerified && sessionStorage.getItem('dmy_verified') === '1') {
        initialVerified = true;
      }
    }
  } catch (e) {
    // ignore
  }

  let humanVerified = $state(initialVerified);

  // Debounce timer handle for save operations
  let saveTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Collect current store values into a StoragePayload for persistence.
   */
  function buildPayload(): StoragePayload {
    return {
      name: localStorage.getItem('dmy_name') ?? '',
      records: $records,
      step: $currentStep,
      checklistId: $selectedChecklistId,
      committeeComments: $committeeComments,
    };
  }

  /**
   * Schedule a debounced save to the active adapter.
   * Multiple rapid state changes are collapsed into one network/disk write.
   */
  function scheduleSave(adapter: StorageAdapter) {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      storageSyncing.set(true);
      try {
        await adapter.save(buildPayload());
      } catch (err) {
        console.warn('[App] save error:', err);
      } finally {
        storageSyncing.set(false);
      }
    }, 1000);
  }

  onMount(async () => {
    // Check sessionStorage verification flag
    if (sessionStorage.getItem('dmy_verified') === '1') {
      humanVerified = true;
    }

    // ── 1. Initialise storage adapter (remote or local) ──────────────────
    const { adapter, mode, localAdapter } = await initStorage();
    storageAdapter.set(adapter);
    storageMode.set(mode);

    // ── 2. Load persisted state ───────────────────────────────────────────
    const payload = await adapter.load();

    if (payload) {
      if (payload.name) humanVerified = true;
      records.set(payload.records ?? {});
      committeeComments.set(payload.committeeComments ?? {});

      // migrate legacy 'summary' step to 'checklist'
      const stepToRestore = payload.step === 'summary' ? 'checklist' : payload.step;
      if (payload.step && payload.name) {
        currentStep.set(stepToRestore as any);
      } else {
        currentStep.set('selection');
      }

      if (payload.checklistId) selectedChecklistId.set(payload.checklistId);
    } else {
      currentStep.set('selection');
    }

    // ── 3. Load font size (always from localStorage regardless of mode) ───
    const savedFont = localAdapter.loadFontSize();
    if (savedFont !== null) fontSize.set(savedFont);

    // ── 4. Subscribe to stores and auto-save on change ───────────────────
    records.subscribe(() => scheduleSave(adapter));
    currentStep.subscribe(() => scheduleSave(adapter));
    selectedChecklistId.subscribe(() => scheduleSave(adapter));
    committeeComments.subscribe(() => scheduleSave(adapter));

    // Font size is always saved locally (UI preference, not shared data)
    fontSize.subscribe(size => {
      try {
        document.documentElement.style.setProperty('--base-font-size', `${size}px`);
        localAdapter.saveFontSize(size);
      } catch (e) {
        // ignore (e.g., SSR)
      }
    });
  });

  function onHumanVerified() {
    humanVerified = true;
    sessionStorage.setItem('dmy_verified', '1');
  }
</script>

<svelte:head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
  <meta name="mobile-web-app-capable" content="yes" />
  <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
</svelte:head>

<div class="min-h-screen h-screen flex flex-col bg-linear-to-b from-slate-50 to-slate-100 overflow-hidden">
  <Header />

  <main class="grow overflow-y-auto overscroll-contain px-3 sm:px-4 py-2 sm:py-0 mx-auto w-full max-w-none">
    <div class="bg-white rounded-2xl sm:rounded-3xl shadow-lg p-1 sm:p-1 mb-1">
      {#if !humanVerified}
        <HumanVerify on:verified={onHumanVerified} />
      {:else if $currentStep === 'selection'}
        <Selection />
      {:else if $currentStep === 'checklist'}
        <ChecklistView />
      {:else if $currentStep === 'print'}
        <PrintView />
      {/if}
    </div>
  </main>

  <!-- Footer intentionally removed to save first-screen space for screenshots -->
</div>

<style>
  :global(html) {
    font-size: var(--base-font-size);
  }
  :global(html, body) {
    margin: 0;
    padding: 0;
    overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Traditional Chinese', 'Microsoft JhengHei', sans-serif;
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: transparent;
  }

  :global(input, select, textarea) {
    font-family: inherit; /* keep font-family, allow utility classes to set size */
  }
</style>
